import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';

/**
 * Unified status flag management hook for green/yellow/red status tracking.
 * Replaces 5+ implementations of status flag buttons and updates.
 * 
 * Used by: Mission, Objective, Activity (key_results_status), DailyTask
 * 
 * Usage:
 * const { updateFlag, isUpdating } = useStatusFlag({
 *   entityType: 'Mission',
 *   queryKey: 'missions',
 *   statusField: 'status_flag'
 * });
 * 
 * <StatusFlagButtons
 *   currentFlag={mission.status_flag}
 *   onChange={(flag) => updateFlag(mission.id, flag)}
 * />
 */

// Flag colors and styles
export const FLAG_CONFIG = {
  green: {
    label: 'On Track',
    shortLabel: 'Green',
    activeClass: 'bg-emerald-500',
    inactiveClass: 'bg-emerald-500/30 hover:bg-emerald-500/50',
    badgeClass: 'bg-emerald-500/20 text-emerald-400',
    color: '#10b981'
  },
  yellow: {
    label: 'At Risk',
    shortLabel: 'Yellow',
    activeClass: 'bg-amber-500',
    inactiveClass: 'bg-amber-500/30 hover:bg-amber-500/50',
    badgeClass: 'bg-amber-500/20 text-amber-400',
    color: '#f59e0b'
  },
  red: {
    label: 'Off Track',
    shortLabel: 'Red',
    activeClass: 'bg-red-500',
    inactiveClass: 'bg-red-500/30 hover:bg-red-500/50',
    badgeClass: 'bg-red-500/20 text-red-400',
    color: '#ef4444'
  },
  gray: {
    label: 'Not Started',
    shortLabel: 'Gray',
    activeClass: 'bg-slate-500',
    inactiveClass: 'bg-slate-500/30 hover:bg-slate-500/50',
    badgeClass: 'bg-slate-500/20 text-slate-400',
    color: '#64748b'
  }
};

export const FLAG_OPTIONS = ['green', 'yellow', 'red'];
export const FLAG_OPTIONS_WITH_GRAY = ['green', 'yellow', 'red', 'gray'];

/**
 * Get flag styling classes
 */
export function getFlagClasses(flag, isActive) {
  const config = FLAG_CONFIG[flag] || FLAG_CONFIG.gray;
  return isActive ? config.activeClass : config.inactiveClass;
}

/**
 * Get flag badge classes
 */
export function getFlagBadgeClass(flag) {
  const config = FLAG_CONFIG[flag] || FLAG_CONFIG.gray;
  return config.badgeClass;
}

/**
 * Main status flag hook
 */
export default function useStatusFlag({
  entityType,
  queryKey,
  statusField = 'status_flag',
  onSuccess,
  onError
}) {
  const queryClient = useQueryClient();

  const updateFlagMutation = useMutation({
    mutationFn: async ({ id, flag, additionalData = {} }) => {
      const updateData = {
        [statusField]: flag,
        ...additionalData
      };
      
      // Add timestamp for status changes
      if (statusField === 'key_results_status') {
        updateData.key_results_status_updated_at = new Date().toISOString();
      }
      
      return base44.entities[entityType].update(id, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      onSuccess?.();
    },
    onError: (error) => {
      console.error(`Failed to update ${entityType} status flag:`, error);
      onError?.(error);
    }
  });

  const updateFlag = useCallback((id, flag, additionalData = {}) => {
    updateFlagMutation.mutate({ id, flag, additionalData });
  }, [updateFlagMutation]);

  const updateFlagAsync = useCallback((id, flag, additionalData = {}) => {
    return updateFlagMutation.mutateAsync({ id, flag, additionalData });
  }, [updateFlagMutation]);

  return {
    updateFlag,
    updateFlagAsync,
    isUpdating: updateFlagMutation.isPending,
    error: updateFlagMutation.error
  };
}

/**
 * Calculate aggregate status from child items
 */
export function calculateAggregateStatus(items, statusField = 'status_flag') {
  if (!items || items.length === 0) return null;
  
  const counts = { green: 0, yellow: 0, red: 0, gray: 0 };
  
  items.forEach(item => {
    const flag = item[statusField] || 'gray';
    if (counts[flag] !== undefined) {
      counts[flag]++;
    }
  });
  
  // If any red, aggregate is red
  if (counts.red > 0) return 'red';
  // If any yellow, aggregate is yellow
  if (counts.yellow > 0) return 'yellow';
  // If all green, aggregate is green
  if (counts.green === items.length) return 'green';
  // Default to gray (not started)
  return 'gray';
}

/**
 * Get status distribution for progress bars
 */
export function getStatusDistribution(items, statusField = 'status_flag') {
  const total = items?.length || 0;
  if (total === 0) return { green: 0, yellow: 0, red: 0, gray: 0, total: 0 };
  
  const counts = { green: 0, yellow: 0, red: 0, gray: 0, total };
  
  items.forEach(item => {
    const flag = item[statusField] || 'gray';
    if (counts[flag] !== undefined) {
      counts[flag]++;
    }
  });
  
  return counts;
}

/**
 * Get percentage distribution for charts
 */
export function getStatusPercentages(items, statusField = 'status_flag') {
  const dist = getStatusDistribution(items, statusField);
  const total = dist.total || 1;
  
  return {
    green: (dist.green / total) * 100,
    yellow: (dist.yellow / total) * 100,
    red: (dist.red / total) * 100,
    gray: (dist.gray / total) * 100
  };
}